/* eslint-disable */
// @ts-nocheck

import { ProductDetailsPageComputedState } from './ProductDetailsPageComputedState';
import {
  CreditIncentivesEligibility,
  ProductDetailsPageState,
} from './ProductDetailsPageState';
import { formatMoneyForLocale } from '@stitch-fix/i18n';
import {
  AVAILABLE_STOCK,
  LIMITED_STOCK,
  OUT_OF_STOCK,
} from '../../../helpers/availability';
import { businessLineToRoute } from '../../../helpers/routingHelper';
import { ColorOption } from './ColorOption';
import { ShopCategories } from './ShopCategories';
import { shopCategoriesPath } from '../../../utils/urlUtils';

// TODO: Currently we are setting up the framework to rework our state to ingest
// the new Shop Product Model data. The guts of this file have been removed for now.
// Soon this file will be updated to build a new version of computed state utilizing
// the shape of the data in the Shop Product Model. After completing this file,
// please re-enable type-checking and eslint (currently disabled at top of file).

const colorAvailabilityBySize = (
  uniqueColorId: string,
  colorOptionsBySize?: {
    name: string;
    uniqueColorId: string;
    inventory: { availability: string };
  }[],
  selectedSize: string,
) => {
  if (!colorOptionsBySize.length) {
    return {};
  }

  const colorBySelectedSize = colorOptionsBySize?.find(
    data => data.uniqueColorId === uniqueColorId,
  );
  const returnValue = {};

  returnValue[selectedSize] =
    !!colorBySelectedSize &&
    colorBySelectedSize?.inventory?.availability !== OUT_OF_STOCK;

  return returnValue;
};

export function colorOptions_implementation(
  colorOptions?: {
    name: string;
    position: number;
    uniqueColorId: string;
    swatch: { hex: string; url?: string | null };
    inventory: { availability: string };
  }[],
  recommendedColorOptionsBySize?: {
    name: string;
    uniqueColorId: string;
    inventory: { availability: string };
  }[],
  colorOptionsBySize?: {
    name: string;
    uniqueColorId: string;
    inventory: { availability: string };
  }[],
  selectedSize: string,
  defaultVariantUniqueColorId: string,
  selectedUniqueColorIdHistory: string[],
  onRecommendedSizesTab: boolean,
) {
  const recommendedColorOptionsInStock = recommendedColorOptionsBySize
    ?.filter(recommendedOption => {
      return [AVAILABLE_STOCK, LIMITED_STOCK].includes(
        recommendedOption.inventory?.availability,
      );
    })
    .map(recommendedOption => {
      return recommendedOption?.uniqueColorId;
    });

  // NOTE: There's a special case where a recommended size/inseam for the selected SKU doesn't exist in shop product model when on the
  // recommended sizes tab. This causes colorOptionsBySize to be an empty array. We're combining the recommendedColorOptionsBySize below
  // to handle this case so that color swatch stock statuses are shown properly when a recommended size/inseam does exist for other color options.
  const allColorOptionsBySize = onRecommendedSizesTab
    ? colorOptionsBySize?.concat(recommendedColorOptionsBySize)
    : colorOptionsBySize;

  return (
    colorOptions
      ?.map(option => ({
        label: option.name,
        swatch: option.swatch,
        position: option.position,
        uniqueColorId: option.uniqueColorId,
        availability: [AVAILABLE_STOCK, LIMITED_STOCK].includes(
          option.inventory?.availability,
        ),
        availabilityBySize: colorAvailabilityBySize(
          option.uniqueColorId,
          allColorOptionsBySize,
          selectedSize,
        ),
      }))
      .filter(option => {
        return (
          (option.availability &&
            (recommendedColorOptionsInStock?.length < 1 ||
              recommendedColorOptionsInStock?.includes(
                option.uniqueColorId,
              ))) ||
          option.uniqueColorId === defaultVariantUniqueColorId ||
          selectedUniqueColorIdHistory.includes(option.uniqueColorId)
        );
      })
      .sort((color1: ColorOption, color2: ColorOption) => {
        return color1.position > color2.position ? 1 : -1;
      }) || []
  );
}

export function sizeOptions_implementation(
  sizeOptions?: {
    name: string;
    position: number;
    inventory: { availability: string };
  }[],
) {
  return (
    sizeOptions
      ?.map(option => ({
        label: option.name,
        position: option.position,
        availability: [AVAILABLE_STOCK, LIMITED_STOCK].includes(
          option.inventory?.availability,
        ),
      }))
      .sort((size1: SizeOption, size2: SizeOption) => {
        return size1.position > size2.position ? 1 : -1;
      }) || []
  );
}

export function recommendedSizeOptions_implementation(
  recommendedSizeOptions?: {
    sku: {
      id: string;
      businessLine: string;
      color: string;
      departmentDisplayName: string;
      inventory: {
        availability: string;
        quantityAvailable: string;
      };
      price: {
        currency: string;
        value: number;
      };
      saved: boolean;
      size: string;
      sizeOrder: string;
      inseam: string;
    };
  }[],
) {
  return (
    recommendedSizeOptions?.map(option => ({
      label: option.shopProductBySkuId?.variantBySkuId?.size?.name,
    })) || []
  );
}

export function sizesIncludeTall_implementation(
  sizeOptions: {
    label: string;
    position: number;
  }[],
): boolean {
  return sizeOptions.some(option => option.label.toUpperCase().includes('TA'));
}

export function sizesIncludeShort_implementation(
  sizeOptions: {
    label: string;
    position: number;
  }[],
): boolean {
  return sizeOptions.some(option => option.label.toUpperCase().includes('SH'));
}

export function sizesIncludePlus_implementation(
  sizeOptions: {
    label: string;
    position: number;
  }[],
): boolean {
  const plusSizes = ['1X', '2X', '3X'];

  return sizeOptions?.some(option =>
    plusSizes.includes(option.label.toUpperCase()),
  );
}

export function sizesIncludePetite_implementation(
  sizeOptions: {
    label: string;
    position: number;
  }[],
): boolean {
  return sizeOptions.some(option => option.label.toUpperCase().includes('P'));
}

export function recommendedSizesIncludeTall_implementation(
  sizeOptions: {
    label: string;
  }[],
): boolean {
  return sizeOptions.some(option => option.label.toUpperCase().includes('TA'));
}

export function recommendedSizesIncludeShort_implementation(
  sizeOptions: {
    label: string;
  }[],
): boolean {
  return sizeOptions.some(option => option.label.toUpperCase().includes('SH'));
}

export function recommendedSizesIncludePlus_implementation(
  sizeOptions: {
    label: string;
  }[],
): boolean {
  const plusSizes = ['1X', '2X', '3X'];

  return sizeOptions?.some(option =>
    plusSizes.includes(option.label.toUpperCase()),
  );
}

export function recommendedSizesIncludePetite_implementation(
  sizeOptions: {
    label: string;
  }[],
): boolean {
  return sizeOptions.some(option => option.label.toUpperCase().includes('P'));
}

export function eligibleForCreditIncentives_implementation(
  creditIncentivesEligibility: CreditIncentivesEligibility | null,
): boolean {
  const { configParams } = creditIncentivesEligibility || {};

  if (!configParams || !creditIncentivesEligibility) {
    return false;
  }

  const eligibleForExperiment = configParams.some(
    ({ name, value }) =>
      name === 'eng.kufak.freestyle.credit_incentive.test_case_1' &&
      value === 'Accessible',
  );

  return eligibleForExperiment;
}

export function eligibleForShoppingAcrossBusinessLines_implementation(
  shoppingAcrossBusinessLinesEligibility: ShoppingAcrossBusinessLinesEligibility | null,
): boolean {
  const { eligibility } = shoppingAcrossBusinessLinesEligibility || {};
  if (!eligibility || !shoppingAcrossBusinessLinesEligibility) {
    return false;
  }

  const eligibleForExperiment = eligibility.some(
    ({ name, value }) =>
      name === 'eng.kufak.shopping_across_lines_of_business' &&
      value === 'treatment',
  );

  return eligibleForExperiment;
}

export function inseamOptions_implementation(
  inseamOptions: {
    name: string;
    position: number;
    inventory: { availability: string };
  }[],
) {
  return (
    inseamOptions
      ?.map(option => ({
        label: option.name,
        position: option.position,
        availability: [AVAILABLE_STOCK, LIMITED_STOCK].includes(
          option.inventory?.availability,
        ),
      }))
      .sort((inseam1, inseam2) => {
        return inseam1.position > inseam2.position ? 1 : -1;
      }) || []
  );
}

export function allSkusOutOfStock_implementation(
  colorOptions?: {
    availability: boolean;
  }[],
) {
  return colorOptions.every(option => option.availability === false);
}

export function shopCategoriesCards_implementation(
  businessLine: string | null,
  shopCategories?: ShopCategories | null,
  source: string | null,
  queryId: string | null,
  gated: boolean,
) {
  const categorySetId = shopCategories?.categorySet?.id;
  const cards = shopCategories?.categorySet?.cards;

  if (!shopCategories || !cards?.length || !businessLine) {
    return [];
  }

  const shopCategoriesPathRoot = gated
    ? '/shop/categories'
    : `/${businessLineToRoute[businessLine]}/clothing/${categorySetId}`;

  return cards.map(card => ({
    categoryId: card?.category?.id,
    href: shopCategoriesPath(
      `${shopCategoriesPathRoot}/${card?.category?.id}`,
      {
        source,
        subSource: 'pdp-categories-carousel',
        queryId,
      },
    ),
    // Note: We can't just set image to card?.image because of TypeScript
    // conversion errors with CategoriesCarousel component
    image: {
      altText: card?.image?.altText || '',
      baseUrl: card?.image?.baseUrl || '',
      mediaPath: card?.image?.mediaPath || '',
    },
    title: card?.title || '',
  }));
}

export function shopCategoriesEndCard_implementation(
  businessLine: string | null,
  shopCategories?: ShopCategories | null,
  source: string | null,
  queryId: string | null,
  gated: boolean,
) {
  const categorySetId = shopCategories?.categorySet?.id;
  const endCard = shopCategories?.endCard;
  const image = endCard?.image;

  if (!shopCategories || !endCard || !businessLine) {
    return undefined;
  }

  const shopCategoriesEndCardPathRoot = gated
    ? `/shop/categories/${categorySetId}`
    : `/${businessLineToRoute[businessLine]}/clothing/${categorySetId}`;

  return {
    // Note: We can't just set image to card?.image because of TypeScript conversion errors
    image: {
      altText: image?.altText || '',
      baseUrl: image?.baseUrl || '',
      mediaPath: image?.mediaPath || '',
    },
    cta: {
      href: shopCategoriesPath(shopCategoriesEndCardPathRoot, {
        source,
        subSource: 'pdp-categories-carousel',
        queryId,
      }),
      text: endCard?.cta?.text || '',
    },
  };
}

/**
 * Computed state properties for the ShopProductDetailsPageContext
 *
 * This function creates and returns various pieces of the ShopProductDetailsPageContext.
 *
 * @param state
 */
export default function useShopProductDetailsPageContextComputedState(
  state: ProductDetailsPageState,
): ProductDetailsPageComputedState {
  const shopProduct = state.shopProduct;
  const locale = state.locale;
  const currentSkuPriceAndInventory =
    state.priceAndInventoryData?.variantBySkuId;
  const sizeOptions = sizeOptions_implementation(state.shopSizeOptions || []);
  const recommendedSizeOptions = recommendedSizeOptions_implementation(
    state.recommendedSizes || [],
  );

  /**
   * The Document's title.
   * [businessLine]'s [brandName] [productName] | Stitch Fix
   * Women’s Levi’s 711 Skinny Jeans | Stitch Fix
   *
   * NOTE: Unexpected business lines are removed from the title.
   */
  let title = '';

  if (state.productBusinessLine) {
    const FormattedBusinessLineMappings = {
      WOMENS: "Women's",
      MENS: "Men's",
      KIDS: "Kid's",
      // EXPL: SHELL shouldn't be possible for product business lines.
      SHELL: '',
    };

    const titleArray = [
      FormattedBusinessLineMappings[state.productBusinessLine],
      shopProduct?.brand?.name,
      shopProduct?.name,
      '| Stitch Fix',
    ];

    title = titleArray.join(' ');
  }

  /**
   * A string. Converts the sku's price object into a human readable format like "$25.00"
   */
  const formattedPrice = currentSkuPriceAndInventory
    ? formatMoneyForLocale(locale, currentSkuPriceAndInventory.price, { subunits: true })
    : '';

  /**
   * A boolean value.
   *
   * True if the sizes include any plus sizes
   */
  const sizesIncludePlus = sizesIncludePlus_implementation(sizeOptions);
  /**
   * A boolean value.
   *
   * True if the sizes include any petite sizes
   */
  const sizesIncludePetite = sizesIncludePetite_implementation(sizeOptions);

  /**
   * A boolean value.
   *
   * True if the sizes include any tall sizes
   */
  const sizesIncludeTall = sizesIncludeTall_implementation(sizeOptions);

  /**
   * A boolean value.
   *
   * True if the sizes include any short sizes
   */
  const sizesIncludeShort = sizesIncludeShort_implementation(sizeOptions);

  /**
   * A boolean value.
   *
   * True if the recommended sizes include any plus sizes
   */
  const recommendedSizesIncludePlus =
    recommendedSizesIncludePlus_implementation(recommendedSizeOptions);
  /**
   * A boolean value.
   *
   * True if the recommended sizes include any petite sizes
   */
  const recommendedSizesIncludePetite =
    recommendedSizesIncludePetite_implementation(recommendedSizeOptions);

  /**
   * A boolean value.
   *
   * True if the recommended sizes include any tall sizes
   */
  const recommendedSizesIncludeTall =
    recommendedSizesIncludeTall_implementation(recommendedSizeOptions);

  /**
   * A boolean value.
   *
   * True if the recommended sizes include any short sizes
   */
  const recommendedSizesIncludeShort =
    recommendedSizesIncludeShort_implementation(recommendedSizeOptions);

  /**
   * An array objects of labels (strings) and availability (boolean)
   *
   * Example: [
      { label: 'inseam 3', availability: false },
      { label: 'inseam 4', availability: false },
    ]
  */
  const inseamOptions = inseamOptions_implementation(
    state.shopInseamOptions || [],
  );

  const effectivelySelectedSizeLabel = state.recommendedSkuSizeLabel || '';
  const effectivelySelectedInseamLabel = state.recommendedSkuInseamLabel || '';

  const isRecommendedSizeOutOfStock =
    effectivelySelectedSizeLabel !== '' &&
    sizeOptions.length > 0 &&
    !sizeOptions?.filter(
      inseamOption => inseamOption.label == effectivelySelectedSizeLabel,
    )[0]?.availability;

  const isRecommendedInseamOutOfStock =
    effectivelySelectedInseamLabel !== '' &&
    inseamOptions.length > 0 &&
    !inseamOptions?.filter(
      inseamOption => inseamOption.label == effectivelySelectedInseamLabel,
    )[0]?.availability;

  /**
   * An array of color objects, sorted by hex.
   *
   * Example:
   * [{ label: 'Blue', swatch: { hex: #0000ff, url: null }, availability: true }]
   */
  const colorOptions = colorOptions_implementation(
    state.shopColorOptions || [],
    state.shopRecommendedColorOptionsBySize || [],
    state.shopColorOptionsBySize || [],
    state.onRecommendedSizesTab
      ? state.recommendedSkuSizeLabel || ''
      : state.selectedSizeLabel || state.fallbackSizeLabel || '',
    state.selectedUniqueColorId || state.fallbackUniqueColorId || '',
    state.selectedUniqueColorIdHistory,
    state.onRecommendedSizesTab,
  );

  /**
   * A string value.
   *
   * The selectedVariant's availability (AVAILABLE_STOCK, OUT_OF_STOCK, LIMITED_STOCK)
   */
  const selectedVariantAvailability =
    currentSkuPriceAndInventory?.inventory?.availability;

  /**
   * A boolean value.
   *
   * True if a sku is considered "limited stock", false otherwise.
   */
  const isSkuLimitedStock = selectedVariantAvailability === LIMITED_STOCK;

  const effectivelySelectedColorOption =
    state.selectedUniqueColorId ||
    state.selectedColorLabel ||
    state.fallbackUniqueColorId ||
    state.fallbackColorLabel ||
    '';

  const isSelectedColorOutOfStock =
    effectivelySelectedColorOption !== '' &&
    colorOptions.length > 0 &&
    Object.keys(
      colorOptions?.filter(
        colorOption =>
          colorOption.uniqueColorId == effectivelySelectedColorOption ||
          colorOption.label == effectivelySelectedColorOption,
      )[0]?.availabilityBySize || {},
    ).length > 0 &&
    !colorOptions?.filter(
      colorOption =>
        colorOption.uniqueColorId == effectivelySelectedColorOption ||
        colorOption.label == effectivelySelectedColorOption,
    )[0]?.availabilityBySize[
      state.selectedSizeLabel || state.fallbackSizeLabel || ''
    ];

  const effectivelySelectedSizeOption = state.selectedSizeLabel || '';

  const isSelectedSizeOutOfStock =
    effectivelySelectedSizeOption !== '' &&
    sizeOptions.length > 0 &&
    !sizeOptions?.filter(
      sizeOption => sizeOption.label == effectivelySelectedSizeOption,
    )[0]?.availability;

  const effectivelySelectedInseamOption = state.selectedInseamLabel || '';

  const isSelectedInseamOutOfStock =
    effectivelySelectedInseamOption !== '' &&
    inseamOptions.length > 0 &&
    !inseamOptions?.filter(
      inseamOption => inseamOption.label == effectivelySelectedInseamOption,
    )[0]?.availability;

  // NOTE: Sometimes a recommended size/inseam combo may not exist in shop product model for the currently selected SKU. We then default
  // to selecting the first size/inseam that does exist in SPM. This is misleading when on the recommended sizes tab because a client
  // may unknowingly purchase a SKU thinking they are buying the recommended size/inseam when in reality they are buying the first option
  // in the all sizes tab. Because of this, we need to make sure to check the recommended size/inseam is actually available for the
  // selected SKU when on the recommended size tab so that the stock status is shown properly.
  const isRecommendedSizeAndInseamOutOfStock =
    !!state.selectedSkuId &&
    (isSelectedColorOutOfStock ||
      isRecommendedSizeOutOfStock ||
      isRecommendedInseamOutOfStock);

  const isSelectedSkuOutOfStock =
    !!state.selectedSkuId &&
    (isSelectedColorOutOfStock ||
      isSelectedSizeOutOfStock ||
      isSelectedInseamOutOfStock);

  /**
   * A boolean value.
   *
   * True if a sku is considered "out of stock", false otherwise.
   * Some skus (e.g. very old skus) have missing availability.  We assume these skus are out of stock.
   */
  const isSkuOutOfStock = state.onRecommendedSizesTab
    ? isRecommendedSizeAndInseamOutOfStock
    : isSelectedSkuOutOfStock;

  /**
   * A boolean value.
   *
   * True if the stock status label should be shown on the page. Should only show label if a sku is selected.
   */
  const showStockStatus =
    !!state.selectedSkuId && (isSkuLimitedStock || isSkuOutOfStock);

  /**
   * A boolean value.
   *
   * If all color options are out of stock, we can deduce that all skus
   * are out of stock, and return true.
   */
  const allSkusOutOfStock = allSkusOutOfStock_implementation(colorOptions);

  /**
   * A boolean value.
   *
   * True if either visitor is eligible for
   * credit incentives experiment 1 or client is eligible for experiment AND
   * has no previous purchases
   */
  const eligibleForCreditIncentives =
    eligibleForCreditIncentives_implementation(
      state.creditIncentivesEligibility,
    );

  /**
   * A boolean value.
   *
   * True if client is eligible for
   * shopping across business lines
   */
    const eligibleForShoppingAcrossBusinessLines =
    eligibleForShoppingAcrossBusinessLines_implementation(
      state.shoppingAcrossBusinessLinesEligibility
    )
    /**

  /**
   * An array of objects representing shop categories carousel cards
   *
   * Example:
   * [{
   *   categoryId: 'some-category-card-uuid',
   *   href: 'https://www.example.com/card-1',
   *   image: {
   *     altText: 'some alt text',
   *     baseUrl: 'https://www.some-base-url.com/images/',
   *     mediaPath: '/media-path.png',
   *   },
   *   title: 'Casual Tops',
   * }]
   */
  const shopCategoriesCards = shopCategoriesCards_implementation(
    state.productBusinessLine,
    state.shopCategories,
    state.source,
    state.queryId,
    state.gated,
  );

  /**
   * An object representing the shop categories carousel end card
   *
   * Example:
   * {
   *   image: {
   *     altText: 'some alt text',
   *     baseUrl: 'https://www.some-base-url.com/images/',
   *     mediaPath: '/media-path.png',
   *   },
   *   cta: {
   *     href: 'https://www.example.com/shop-more'
   *     text: 'Shop more',
   *   },
   * }
   */
  const shopCategoriesEndCard = shopCategoriesEndCard_implementation(
    state.productBusinessLine,
    state.shopCategories,
    state.source,
    state.queryId,
    state.gated,
  );

  return {
    title,
    formattedPrice,
    isSkuLimitedStock,
    isSkuOutOfStock,
    selectedVariantAvailability,
    allSkusOutOfStock,
    showStockStatus,
    colorOptions,
    sizeOptions,
    inseamOptions,
    shopCategoriesCards,
    shopCategoriesEndCard,
    sizesIncludePetite,
    sizesIncludePlus,
    sizesIncludeShort,
    sizesIncludeTall,
    recommendedSizesIncludePetite,
    recommendedSizesIncludePlus,
    recommendedSizesIncludeShort,
    recommendedSizesIncludeTall,
    eligibleForShoppingAcrossBusinessLines,
    eligibleForCreditIncentives,
    isRecommendedSizeAndInseamOutOfStock,
  };
}
